/** The system-level rights of the permission grammar (`module.<id>.read|write|create|delete`). */
export type SystemLevelRight = 'read' | 'write' | 'create' | 'delete';

/**
 * True when the permission list grants a system-level right on a module: some entry names the
 * module, carries `.<right>`, and contains neither `.company` nor `.employee`. System-level is the
 * "every entry" tier; the company- and employee-level variants only reach the user's own company or
 * own records.
 *
 * ROLE EDITOR ONLY (`UserRolesService.checkIfHasSystem{View,Edit,Create,Delete}Permission` delegate
 * here): it reads a ROLE's permission list to tick the system-level boxes, one rule for every right. A
 * surface that gates the VIEWER on a system-level right (export, import, the documents scope switch)
 * asks `UserStore.hasExactPermission` instead — exact `module.<id>.<right>`, with the `system.admin`
 * claim counting — so every viewer gate gives one answer. This substring reading is not that answer:
 * it has no `system.admin` and would accept a custom suffix such as `module.<id>.liveplanning.read`.
 *
 * `UserStore.checkIfHas*Permission` cannot express the tier either. It goes through a memoized
 * `hasPermission()` that matches one closed union of suffixes by substring, so
 * `module.<id>.company.read` also answers "yes" to `.read`; telling the tiers apart needs the
 * negative match, and widening the suffix union for that would break the cache key's uniqueness
 * (see the comment on `PermissionSuffix`).
 */
export function hasSystemLevelPermission(
  permissions: readonly string[] | null | undefined,
  moduleId: string,
  right: SystemLevelRight
): boolean {
  if (!permissions) {
    return false;
  }

  return permissions.some(
    permission =>
      permission.includes(moduleId) &&
      permission.includes(`.${right}`) &&
      !permission.includes('.company') &&
      !permission.includes('.employee')
  );
}
